<script lang="ts">
import { templateRef, useElementSize } from '@vueuse/core'
import { gsap } from 'gsap'
import { computed, defineComponent, nextTick, watch } from 'vue'

import { useBreakpoint } from '@/functions/useBreakpoint'

export default defineComponent({
  name: 'LayerGrid',
  props: {
    defaultVisible: { type: Boolean, default: false },
    squareSize: { type: [Number, String], default: 'auto' }
  },
  emits: ['refresh'],
  setup (props, { attrs, slots, emit, expose }) {
    const wrapper = templateRef<HTMLElement>('wrapper')
    const { width, height } = useElementSize(wrapper)
    const breakpoint = useBreakpoint()
    const size = computed(() => typeof props.squareSize === 'number'
      ? props.squareSize
      : breakpoint.md ? 125 : 100
    )

    const numcols = computed(() => ~~(width.value / size.value))
    const numrows = computed(() => ~~(height.value / size.value))

    watch([numcols, numrows], async ([cols, rows]) => {
      if (!cols || !rows) return
      await nextTick()
      emit('refresh', gsap.utils.toArray('span', wrapper.value))
    }, { flush: 'post' })

    return {
      numcols,
      numrows
    }
  }
})
</script>

<template>
  <span
    ref="wrapper"
    class="grid w-full h-full pointer-events-none"
    :class="{ 'grid-visible': defaultVisible }"
  >
    <span v-for="i in numcols*numrows" :key="i"></span>
  </span>
</template>

<style lang="scss" scoped>
.grid {
  grid-template-columns: repeat(v-bind(numcols), minmax(0, 1fr));
  grid-template-rows: repeat(v-bind(numrows), minmax(0, 1fr));

  & > span {
    display: block;
    background-color: currentColor;
  }

  &:not(.grid-visible) > span {
    opacity: 0;
  }
}
</style>
